import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";

interface Feature {
  id: string;
  title?: string;
  description?: string;
  illustration: string;
}

interface FeatureSpotlightProps {
  features: Feature[];
}

/**
 * FeatureSpotlight
 *
 * 3-column grid showcasing key features with illustrations.
 * Uses SpotlightCard for interactive hover effects.
 * Text from translations keyed by feature id.
 */
export function FeatureSpotlight({ features }: FeatureSpotlightProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("featureSpotlight.title");
  const subtitle = t("featureSpotlight.subtitle");

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">Spotlight</SectionLabel>

          <h2 className="mb-6 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="text-base leading-relaxed text-muted-foreground lg:text-lg">{subtitle}</p>
          )}
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {features.map((feature, index) => {
            const featureTitle = t(`featureSpotlight.${feature.id}.title`, {
              defaultValue: feature.title,
            });
            const featureDescription = t(`featureSpotlight.${feature.id}.description`, {
              defaultValue: feature.description,
            });

            return (
              <SpotlightCard
                key={feature.id}
                className={cn(
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
                )}
                style={{
                  transitionDelay: isVisible ? `${index * 80}ms` : "0ms",
                }}
              >
                <SpotlightCardContent className="flex h-full flex-col p-6 lg:p-8">
                  {/* Illustration */}
                  <div className="mb-6 flex aspect-video items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
                    <img
                      src={feature.illustration}
                      alt={featureTitle}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        // Fallback for missing images
                        e.currentTarget.style.display = "none";
                        e.currentTarget.parentElement!.innerHTML = `
                        <div class="flex items-center justify-center h-full text-primary opacity-50">
                          <svg class="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      `;
                      }}
                    />
                  </div>

                  {/* Title */}
                  <h3 className="mb-3 text-lg font-semibold text-foreground lg:text-xl">
                    {featureTitle}
                  </h3>

                  {/* Description */}
                  <p className="flex-1 text-sm leading-relaxed text-muted-foreground lg:text-base">
                    {featureDescription}
                  </p>
                </SpotlightCardContent>
              </SpotlightCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
